import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  cardClass,
  cardHeaderClass,
  cardTitleClass,
  cardBodyClass,
  btnPrimaryClass,
  btnSecondaryClass,
  formControlClass,
  alertErrorClass,
  alertInfoClass,
  alertSuccessClass,
  spinnerClass,
  badgeClass,
  tableClass,
  tableHeadClass,
  tableHeaderCellClass,
  tableBodyClass,
  tableRowClass,
  tableCellClass,
} from "../dashboardStyles";
import {
  GetSmsCenterAction,
  StartSmsPurchaseAction,
  UpdateSmsSettingsAction,
} from "../../../store/action";
import { PlanNameKeys, formatMoney } from "../../../constants/subscription";
import {
  NotificationKindKeys,
  SmsActivityType,
  SmsCreditSource,
  SmsMessageStatus,
  SmsMessageStatusKeys,
  SmsReplyAction,
  SmsReplyKeys,
  formatPricePerSms,
} from "../../../constants/sms";
import { PAYMENT_PROCESSOR } from "../../../constants/legal";
import {
  NotificationChannelTab,
  SettingsTab,
  settingsPath,
} from "../../../constants/settings";
import { notify, notifySaveError } from "../../../utils/notify";

/** A bar that fills to `value / max`; empty when there is nothing to measure. */
const Meter = ({ value, max }) => {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-ink-100">
      <div
        className="h-full rounded-full bg-brand-500"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

/** Status colours: what went wrong is red, what is still moving is grey. */
const STATUS_TONES = {
  [SmsMessageStatus.QUEUED]: "bg-ink-100 text-ink-600",
  [SmsMessageStatus.ACCEPTED]: "bg-ink-100 text-ink-600",
  [SmsMessageStatus.SENT]: "bg-brand-50 text-brand-700",
  [SmsMessageStatus.DELIVERED]: "bg-brand-50 text-brand-700",
  [SmsMessageStatus.FAILED]: "bg-red-50 text-red-700",
};

const REPLY_TONES = {
  [SmsReplyAction.CONFIRMED]: "bg-brand-50 text-brand-700",
  [SmsReplyAction.CANCELLED]: "bg-amber-50 text-amber-700",
  [SmsReplyAction.REFUSED]: "bg-ink-100 text-ink-600",
};

/**
 * The SMS tab of the billing page (`/dashboard/billing/sms`). Billing owns the
 * page title and the tab strip; this is only what goes under them.
 *
 * Three questions, in the order they are asked: how much credit is there,
 * how do I get more, and where did it go. The allowance the plan includes and
 * the credit bought separately are shown apart because they behave apart —
 * the first renews every month and is spent first, the second lasts a year.
 *
 * Buying is two steps, like paying for the plan (SubscriptionPanel): the
 * packages are priced in euro net of VAT, the card is charged in lei with VAT,
 * and the lei figure is stated here before anyone leaves for NETOPIA's page.
 * The credit arrives only when NETOPIA confirms the payment to our server.
 */
const SmsCenter = () => {
  const { t, i18n } = useTranslation();

  const [center, setCenter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [selectedCode, setSelectedCode] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const [starting, setStarting] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await GetSmsCenterAction();
      setCenter(data);
      // The second package is the one the owner chose to recommend.
      setSelectedCode(
        (current) => current ?? data.packages?.[1]?.code ?? data.packages?.[0]?.code ?? null,
      );
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const formatDate = (value) =>
    value ? new Date(value).toLocaleDateString(i18n.language) : "—";

  const selected = center?.packages?.find((p) => p.code === selectedCode);

  const beginPurchase = async () => {
    if (!selected) return;
    setStarting(true);
    try {
      setCheckout({
        ...(await StartSmsPurchaseAction(selected.code)),
        quantity: selected.quantity,
      });
    } catch (err) {
      notifySaveError(err, t("Dashboard.Sms.startError"));
    } finally {
      setStarting(false);
    }
  };

  const saveSettings = async (changes) => {
    const next = {
      lowBalanceAlert: center.lowBalanceAlert,
      lowBalanceThreshold: center.lowBalanceThreshold,
      ...changes,
    };
    setSavingSettings(true);
    try {
      setCenter(await UpdateSmsSettingsAction(next));
      notify.success(t("Dashboard.Sms.settingsSaved"));
    } catch (err) {
      notifySaveError(err, t("Dashboard.Sms.settingsError"));
    } finally {
      setSavingSettings(false);
    }
  };

  // No heading of its own: the page title and the active tab already say where
  // this is. One line on what the tab is for is all it adds.
  const header = (
    <p className="m-0 mb-5 text-fluid-sm text-ink-500">{t("Dashboard.Sms.subtitle")}</p>
  );

  if (loading) {
    return (
      <div>
        {header}
        <div className="flex justify-center py-12">
          <div className={spinnerClass} role="status" />
        </div>
      </div>
    );
  }

  if (error || !center) {
    return (
      <div>
        {header}
        <div className={alertErrorClass} role="alert">
          {t("Dashboard.Sms.loadError")}
        </div>
      </div>
    );
  }

  const purchasedCapacity = center.purchasedLots.reduce(
    (sum, lot) => sum + lot.quantity,
    0,
  );

  const operation = (item) => {
    if (item.type === SmsActivityType.PURCHASE) {
      return t("Dashboard.Sms.purchase", { count: item.quantity });
    }
    return t(NotificationKindKeys[item.kind] ?? "Dashboard.Sms.kinds.reminder");
  };

  const credits = (item) => {
    if (item.type === SmsActivityType.PURCHASE) {
      return <span className="font-semibold text-brand-700">+{item.credits}</span>;
    }
    if (item.source === SmsCreditSource.OWN_NUMBER) {
      return <span className="text-ink-500">{t("Dashboard.Sms.ownNumberCredits")}</span>;
    }
    if (item.credits === 0) {
      return <span className="text-ink-500">0 ({t("Dashboard.Sms.refunded")})</span>;
    }
    return <span className="text-ink-900">−{Math.abs(item.credits)}</span>;
  };

  return (
    <div>
      {header}

      {!center.sendingAvailable && (
        <div className={alertInfoClass} data-testid="sms-not-available">
          <i className="bi bi-info-circle mt-0.5" aria-hidden="true" />
          <span>{t("Dashboard.Sms.notAvailable")}</span>
        </div>
      )}

      {center.usesOwnNumber && (
        <div className={alertSuccessClass} data-testid="sms-own-number">
          <i className="bi bi-phone mt-0.5" aria-hidden="true" />
          <span>{t("Dashboard.Sms.ownNumber")}</span>
        </div>
      )}

      {!center.anySmsNotificationEnabled && (
        <div className={alertInfoClass} data-testid="sms-none-enabled">
          <i className="bi bi-bell-slash mt-0.5" aria-hidden="true" />
          <span>
            {t("Dashboard.Sms.noneEnabled")}{" "}
            <Link
              to={settingsPath({
                tab: SettingsTab.NOTIFICATIONS,
                channel: NotificationChannelTab.SMS,
              })}
              className="font-semibold text-brand-700 underline"
            >
              {t("Dashboard.Sms.settingsLink")}
            </Link>
            .
          </span>
        </div>
      )}

      <div className="flex flex-col gap-6">
        {/* --- Balance ---------------------------------------------------- */}
        <section className={`${cardClass} p-5`} data-testid="sms-balance">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="min-w-0">
              <p className="m-0 text-fluid-sm text-ink-500">
                {t("Dashboard.Sms.available")}
              </p>
              <p className="m-0 mt-1 text-3xl font-bold text-ink-900 tabular-nums">
                {center.totalAvailable}
                <span className="ml-2 text-base font-normal text-ink-500">SMS</span>
              </p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <p className="m-0 text-xs text-ink-500">{t("Dashboard.Sms.currentPlan")}</p>
              <span className={`${badgeClass} bg-brand-50 text-brand-700`}>
                {t(PlanNameKeys[center.plan])}
              </span>
            </div>
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-ink-200 p-4" data-testid="sms-included">
              <p className="m-0 flex items-center gap-2 text-fluid-sm font-semibold text-ink-900">
                <i className="bi bi-gift text-brand-600" aria-hidden="true" />
                {t("Dashboard.Sms.includedTitle")}
              </p>
              {center.includedByAgreement ? (
                <>
                  <p className="m-0 mt-3 text-lg font-bold text-ink-900">
                    {t("Dashboard.Sms.includedByAgreement")}
                  </p>
                  <p className="m-0 mt-2 text-xs text-ink-500">
                    {t("Dashboard.Sms.includedByAgreementHelp")}
                  </p>
                </>
              ) : center.includedPerMonth > 0 ? (
                <>
                  <p className="m-0 mt-3 text-2xl font-bold text-ink-900 tabular-nums">
                    {center.includedRemaining}
                    <span className="text-base font-normal text-ink-500">
                      {" "}/ {center.includedPerMonth}
                    </span>
                  </p>
                  <Meter value={center.includedRemaining} max={center.includedPerMonth} />
                  <p className="m-0 mt-2 text-xs text-ink-500">
                    {t("Dashboard.Sms.includedUsed", {
                      used: center.includedUsed,
                      date: formatDate(center.includedResetsAt),
                    })}
                  </p>
                </>
              ) : (
                <p className="m-0 mt-3 text-fluid-sm text-ink-500">
                  {t("Dashboard.Sms.includedNone")}
                </p>
              )}
            </div>

            <div className="rounded-lg border border-ink-200 p-4" data-testid="sms-purchased">
              <p className="m-0 flex items-center gap-2 text-fluid-sm font-semibold text-ink-900">
                <i className="bi bi-wallet2 text-brand-600" aria-hidden="true" />
                {t("Dashboard.Sms.purchasedTitle")}
              </p>
              <p className="m-0 mt-3 text-2xl font-bold text-ink-900 tabular-nums">
                {center.purchasedRemaining}
              </p>
              <Meter value={center.purchasedRemaining} max={purchasedCapacity} />
              <p className="m-0 mt-2 text-xs text-ink-500">
                {t("Dashboard.Sms.purchasedHelp", { months: center.validityMonths })}
                {center.purchasedLots.length > 0 && (
                  <>
                    {" "}
                    {t("Dashboard.Sms.purchasedExpires", {
                      date: formatDate(center.purchasedLots[0].expiresAt),
                    })}
                  </>
                )}
              </p>
            </div>
          </div>

          <p className="m-0 mt-4 flex items-start gap-2 text-xs text-ink-500">
            <i className="bi bi-info-circle mt-0.5" aria-hidden="true" />
            <span>
              {t("Dashboard.Sms.order")} {t("Dashboard.Sms.segments")}
            </span>
          </p>
        </section>

        {/* Buying on the left, the order and the settings beside it from `xl`
            up; one column, in that order, below it. */}
        <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,20rem)]">
          {/* --- Buying ----------------------------------------------------- */}
          <section className={cardClass} data-testid="sms-buy">
            <div className={cardHeaderClass}>
              <div className="min-w-0">
                <h3 className={cardTitleClass}>{t("Dashboard.Sms.buyTitle")}</h3>
                <p className="m-0 mt-1 text-xs text-ink-500">{t("Dashboard.Sms.buySubtitle")}</p>
              </div>
            </div>
            <div className={cardBodyClass}>
              <fieldset className="m-0 border-0 p-0" disabled={Boolean(checkout)}>
                <legend className="sr-only">{t("Dashboard.Sms.buyTitle")}</legend>
                {/* Two by two at every width: four packages never leave one
                    stranded on a row of its own. */}
                <div className="grid grid-cols-2 gap-3">
                  {center.packages.map((pkg) => {
                    const isSelected = pkg.code === selectedCode;
                    return (
                      <label
                        key={pkg.code}
                        className={`flex min-h-11 min-w-0 cursor-pointer flex-col rounded-lg border p-3 transition sm:p-4 ${
                          isSelected
                            ? "border-brand-600 bg-brand-50"
                            : "border-ink-200 bg-white hover:border-brand-300"
                        }`}
                        data-testid={`sms-package-${pkg.code}`}
                      >
                        <span className="flex items-start justify-between gap-2">
                          <span className="min-w-0 text-xs text-ink-500">
                            {t(`Dashboard.Sms.tiers.${pkg.code}`, { defaultValue: "" })}
                          </span>
                          <input
                            type="radio"
                            name="sms-package"
                            value={pkg.code}
                            checked={isSelected}
                            onChange={() => setSelectedCode(pkg.code)}
                            className="size-4 shrink-0 accent-brand-600"
                          />
                        </span>
                        <span className="mt-2 text-lg font-bold text-ink-900">
                          {pkg.quantity} SMS
                        </span>
                        <span className="mt-1 text-xl font-bold text-ink-900">
                          {formatMoney(pkg.priceCents / 100, pkg.currency)}
                        </span>
                        <span className="mt-1 text-xs text-ink-500">
                          {t("Dashboard.Sms.perSms", { price: formatPricePerSms(pkg) })}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            </div>
          </section>

          <div className="flex flex-col gap-6">
            {/* --- The order -------------------------------------------------- */}
            {(selected || checkout) && (
              <section className={`${cardClass} p-5`} data-testid="sms-order">
                {checkout ? (
                  <div
                    className="rounded-lg border border-brand-200 bg-brand-50 p-4"
                    data-testid="sms-checkout"
                  >
                    <p className="m-0 text-sm font-semibold text-ink-900">
                      {t("Dashboard.Sms.youWillPay", {
                        amount: formatMoney(checkout.amountCents / 100, checkout.currency),
                      })}
                    </p>
                    <p className="m-0 mt-1 text-xs font-semibold text-ink-700">
                      {t("Dashboard.Sms.packageLine", { count: checkout.quantity })}
                    </p>
                    <p className="m-0 mt-1 text-xs text-ink-500">
                      {t("Dashboard.Plan.conversionNote", {
                        source: formatMoney(checkout.sourceAmountCents / 100, checkout.sourceCurrency),
                        rate: checkout.exchangeRate,
                        date: formatDate(checkout.exchangeRateDate),
                        vat: checkout.vatPercent,
                      })}
                    </p>
                    {/* Side by side while the card is wide, stacked once it
                        sits in the narrow right-hand column. */}
                    <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-1">
                      {/* A plain link, not a fetch: the card form is NETOPIA's page,
                          which keeps card details away from this server entirely. */}
                      <a
                        href={checkout.paymentUrl}
                        className={`${btnPrimaryClass} min-h-11`}
                        data-testid="sms-continue-to-payment"
                      >
                        {t("Dashboard.Sms.continue")}
                      </a>
                      <button
                        type="button"
                        className={`${btnSecondaryClass} min-h-11`}
                        onClick={() => setCheckout(null)}
                      >
                        {t("Common.cancel")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div data-testid="sms-summary">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
                      <div className="min-w-0">
                        <p className="m-0 text-xs text-ink-500">{t("Dashboard.Sms.selected")}</p>
                        <p className="m-0 font-semibold text-ink-900">
                          {t("Dashboard.Sms.smsCount", { count: selected.quantity })}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="m-0 text-xs text-ink-500">{t("Dashboard.Sms.subtotal")}</p>
                        <p className="m-0 font-semibold text-ink-900">
                          {formatMoney(selected.priceCents / 100, selected.currency)}{" "}
                          <span className="text-xs font-normal text-ink-500">
                            {t("Dashboard.Sms.plusVat", { vat: center.vatPercent })}
                          </span>
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className={`${btnPrimaryClass} mt-4 min-h-11 w-full`}
                      disabled={starting}
                      onClick={beginPurchase}
                      data-testid="sms-preview-order"
                    >
                      <i className="bi bi-credit-card" aria-hidden="true" />
                      {starting ? t("Dashboard.Sms.preparing") : t("Dashboard.Sms.preview")}
                    </button>
                    <p className="m-0 mt-2 text-xs text-ink-500">{t("Dashboard.Sms.pricesNet")}</p>
                  </div>
                )}

                <a
                  href={PAYMENT_PROCESSOR.url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-4 inline-block"
                >
                  <img
                    src={PAYMENT_PROCESSOR.logo}
                    alt={PAYMENT_PROCESSOR.name}
                    width={910}
                    height={219}
                    className="h-6 w-auto opacity-80 transition hover:opacity-100"
                  />
                </a>
              </section>
            )}

            {/* --- Settings --------------------------------------------------- */}
            <section className={cardClass} data-testid="sms-settings">
              <div className={cardHeaderClass}>
                <h3 className={cardTitleClass}>{t("Dashboard.Sms.settingsTitle")}</h3>
              </div>
              <div className={`${cardBodyClass} flex flex-col gap-4`}>
                <div className="flex min-w-0 items-start gap-3">
                  <input
                    id="smsLowBalanceAlert"
                    type="checkbox"
                    checked={center.lowBalanceAlert}
                    disabled={savingSettings}
                    onChange={(event) => saveSettings({ lowBalanceAlert: event.target.checked })}
                    className="mt-0.5 size-4 shrink-0 cursor-pointer accent-brand-600"
                  />
                  <div className="min-w-0">
                    <label
                      htmlFor="smsLowBalanceAlert"
                      className="block cursor-pointer text-fluid-sm font-medium text-ink-900"
                    >
                      {t("Dashboard.Sms.lowBalanceAlert")}
                    </label>
                    <p className="m-0 mt-0.5 text-xs text-ink-500">
                      {t("Dashboard.Sms.lowBalanceHelp")}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <label
                    className="min-w-0 flex-1 text-xs font-semibold text-ink-600"
                    htmlFor="smsLowBalanceThreshold"
                  >
                    {t("Dashboard.Sms.threshold")}
                  </label>
                  <div className="w-40 shrink-0">
                    <select
                      id="smsLowBalanceThreshold"
                      className={formControlClass}
                      value={center.lowBalanceThreshold}
                      disabled={savingSettings || !center.lowBalanceAlert}
                      onChange={(event) =>
                        saveSettings({ lowBalanceThreshold: Number(event.target.value) })
                      }
                    >
                      {center.allowedThresholds.map((value) => (
                        <option key={value} value={value}>
                          {t("Dashboard.Sms.thresholdOption", { count: value })}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* --- History ------------------------------------------------------------- */}
        <section className={cardClass} data-testid="sms-history">
          <div className={cardHeaderClass}>
            <h3 className={cardTitleClass}>{t("Dashboard.Sms.historyTitle")}</h3>
          </div>
          <div className={cardBodyClass}>
            {center.activity.length === 0 ? (
              <p className="m-0 text-fluid-sm text-ink-500">{t("Dashboard.Sms.noActivity")}</p>
            ) : (
              <table className={tableClass}>
                <thead className={tableHeadClass}>
                  <tr>
                    <th className={tableHeaderCellClass}>{t("Dashboard.Sms.columns.operation")}</th>
                    <th className={tableHeaderCellClass}>{t("Dashboard.Sms.columns.date")}</th>
                    <th className={`${tableHeaderCellClass} text-right`}>
                      {t("Dashboard.Sms.columns.credits")}
                    </th>
                  </tr>
                </thead>
                <tbody className={tableBodyClass}>
                  {center.activity.map((item, index) => (
                    <tr key={`${item.type}-${item.at}-${index}`} className={tableRowClass}>
                      <td className={tableCellClass} data-label={t("Dashboard.Sms.columns.operation")}>
                        <span className="flex min-w-0 flex-col gap-1">
                          <span className="font-medium text-ink-900">{operation(item)}</span>
                          {item.type === SmsActivityType.MESSAGE && (
                            <span className="flex flex-wrap items-center gap-1.5">
                              {item.customerName && (
                                <span className="break-words text-xs text-ink-500">
                                  {item.customerName}
                                </span>
                              )}
                              <span className={`${badgeClass} ${STATUS_TONES[item.status] ?? ""}`}>
                                {t(SmsMessageStatusKeys[item.status] ?? "Dashboard.Sms.status.accepted")}
                              </span>
                              {SmsReplyKeys[item.reply] && (
                                <span className={`${badgeClass} ${REPLY_TONES[item.reply]}`}>
                                  {t(SmsReplyKeys[item.reply])}
                                </span>
                              )}
                            </span>
                          )}
                        </span>
                      </td>
                      <td className={tableCellClass} data-label={t("Dashboard.Sms.columns.date")}>
                        {formatDate(item.at)}
                      </td>
                      <td
                        className={`${tableCellClass} text-right tabular-nums`}
                        data-label={t("Dashboard.Sms.columns.credits")}
                      >
                        {credits(item)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default SmsCenter;
